import { useMemo, FC, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useMutation } from 'react-query';
import { Column, CellProps } from 'react-table';
import { observer } from 'mobx-react-lite';
import { Link } from 'react-router-dom';
import { useStore } from 'hooks/useRootStore';
import { useMyTranslations } from 'hooks/useMyTranslations';
import { IBrandListData } from 'stores/brands/types';
import { PAGE_SIZE, Table } from 'components/Core/Table';
import { AvatarIcon } from 'components/Core/AvatarIcon/AvatarIcon';
import { NoDataComponent } from 'components/Core/NoData/NoData';
// import { IBrandEntityOption } from 'components/Inputs/SelectComponent';
import { StatusCell } from 'components/Core/StatusCell';
import { PolygonsStatusCell } from 'components/Core/PolygonsStatusCell';
import { ComponentWithTooltip } from 'components/Core/ComponentWithTooltip';
import { ReactComponent as EditIcon } from 'assets/edit_icon.svg';
import { ReactComponent as EResyncIcon } from 'assets/e_resync.svg';
import { brandsTableTranslations } from './translations';
import { BrandsTableTransFields } from './types';
import { availableRoles, POINTS_TABLE_LIMIT, DEFAULT_SERVER_VALUE } from './constants';
import styles from './brandsTable.module.scss';
import { IBrandRef } from 'types/common';

const BrandsTable: FC = () => {
  const {
    brandsStore: {
      brands,
      brandsQuery,
      getBrandsData,
      changeBrandStatus,
      changeBrandPolygonsStatus,
      getBrandProductCounts,
    },
    pointsStore: { updatePointsQuery },
    mainStore: { user },
    popUpStore: { openPopUp },
  } = useStore();

  const { t } = useMyTranslations<BrandsTableTransFields>([brandsTableTranslations.name]);

  const navigate = useNavigate();

  const { mutate } = useMutation(getBrandsData, {
    mutationKey: 'getBrandsData',
  });

  const { mutate: mutateProductCounts } = useMutation(getBrandProductCounts, {
    mutationKey: 'getBrandProductCounts',
  });

  useEffect(() => {
    if (user?.selectedCountryCode?.countryCode) {
      mutateProductCounts();
    }
  }, [user?.selectedCountryCode?.countryCode]);

  const onNameClick = useCallback((brand: IBrandRef) => {
    updatePointsQuery({ brand });
    navigate('/points');
  }, []);

  const onEditClick = useCallback((id: string) => {
    navigate(`${id}`);
  }, []);

  const onResyncProductsClick = useCallback(
    (brand: IBrandListData) => {
      openPopUp({
        formName: 'resyncElastic',
        cb: () => ({
          brandId: brand.id,
          numberStop: DEFAULT_SERVER_VALUE,
        }),
      });
    },
    [openPopUp]
  );

  const disabled = useMemo(() => !user || !availableRoles.includes(user.role), [user?.role]);

  const columns: Column<IBrandListData>[] = useMemo(
    () => [
      {
        Header: t('name'),
        id: 'name',
        Cell: ({
          row: {
            original: { id, name, logoUrl },
          },
        }: CellProps<IBrandListData>) => (
          <button
            className={styles.avatar}
            // onClick={() => onNameClick({ id, name, logoUrl, entityType: 'brands' })}
            onClick={() => onNameClick({ id, name, logoUrl })}
            disabled={disabled}>
            <AvatarIcon avatar={logoUrl} name='brand image' size={56} styleProps={{ borderRadius: 8 }} />
            <p className={styles.name}>{name}</p>
          </button>
        ),
        accessor: (row) => row.name,
        width: 9,
        sortType: 'apiSort',
      },
      {
        Header: t('numberOfPoints'),
        id: 'numberOfPoints',
        accessor: (row) => row.numberOfPoints,
        width: 6,
        sortType: 'apiSort',
      },
      {
        Header: () => (
          <div style={{ textAlign: 'center' }}>
            <div>{t('numberOfProducts')}</div>
            <div style={{ fontSize: '0.85em', marginTop: '4px' }}>
              {t('db')}/{t('elastic')}
            </div>
          </div>
        ),
        id: 'numberOfProducts',
        Cell: ({ row: { original } }: CellProps<IBrandListData>) => {
          const { numberOfProducts, numberOfProductsInElastic } = original;
          const isLess = numberOfProductsInElastic < numberOfProducts;
          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span>
                {numberOfProducts} /{' '}
                <span style={{ color: isLess ? 'red' : 'inherit' }}>{numberOfProductsInElastic}</span>
              </span>
              <ComponentWithTooltip
                component={
                  <button
                    className={styles.editBtn}
                    onClick={() => onResyncProductsClick(original)}
                    disabled={disabled || original.status !== 'Active'}>
                    <EResyncIcon width={16} height={16} />
                  </button>
                }
                name='syncIconModal'
                message='Elastic Resync'
                place='top'
              />
            </div>
          );
        },
        accessor: (row) => row.numberOfProducts,
        width: 8,
        sortType: 'apiSort',
      },
      {
        Header: t('numberOfCatalogues'),
        id: 'numberOfCatalogues',
        Cell: ({
          row: {
            original: { id, numberOfCatalogues },
          },
        }: CellProps<IBrandListData>) => (
          <div className={styles.actionBoxBtn}>
            <Link to={`${id}/catalogues`} className={styles.link}>
              {numberOfCatalogues}
            </Link>
          </div>
        ),
        accessor: (row) => row.numberOfCatalogues,
        width: 6,
        sortType: 'apiSort',
      },
      // TEMPORARY UNUSED
      // {
      //   Header: t('owner'),
      //   id: 'owner',
      //   accessor: (row) => row.owner,
      //   width: 6,
      //   sortType: 'apiSort',
      // },
      {
        Header: t('weight'),
        id: 'weight',
        accessor: (row) => row.weight ?? '-',
        width: 4,
        sortType: 'apiSort',
      },
      {
        Header: t('isPolygonsWithProducts'),
        id: 'isPolygonsWithProducts',
        Cell: ({
          row: {
            original: { id, isPolygonsWithProducts },
          },
        }: CellProps<IBrandListData>) => (
          <PolygonsStatusCell
            id={id}
            isPolygonsWithProducts={isPolygonsWithProducts}
            changeStatus={changeBrandPolygonsStatus}
            disabled={disabled}
          />
        ),
        accessor: (row) => row.isPolygonsWithProducts,
        width: 3,
        disableSortBy: true,
      },
      {
        Header: t('status'),
        id: 'status',
        Cell: ({
          row: {
            original: { id, status },
          },
        }: CellProps<IBrandListData>) => (
          <StatusCell id={id} status={status} changeStatus={changeBrandStatus} disabled={disabled} />
        ),
        accessor: (row) => row.status,
        width: 2,
        sortType: 'apiSort',
      },
      {
        Header: t('option'),
        id: 'option',
        Cell: ({ row: { original } }: CellProps<IBrandListData>) => (
          <div className={styles.actionBoxBtn}>
            {/* <Link to={`${id}`} className={styles.editBtn}>
              <EditIcon />
            </Link> */}
            <ComponentWithTooltip
              component={
                <button className={styles.editBtn} onClick={() => onEditClick(original.id)} disabled={disabled}>
                  <EditIcon />
                </button>
              }
              name='editIcon'
              message='edit'
              place='top'
            />
          </div>
        ),
        width: 2,
        disableSortBy: true,
      },
    ],
    // eslint-disable-next-line
    [brands, disabled]
  );

  columns.forEach((item) => (item.Filter = () => null));

  return (
    <>
      {brands && brands.length > 0 ? (
        <Table
          columns={columns as never}
          data={[...brands]}
          simpleList
          isPaginated={brands.length > PAGE_SIZE}
          isManualSorting
          defaultSortId={brandsQuery.orderBy}
          defaultSortOrder={brandsQuery.orderDirection === 'DESC'}
          apiSort={(orderBy, orderDirection) =>
            (brandsQuery?.orderBy !== orderBy || brandsQuery?.orderDirection !== orderDirection) &&
            mutate({ orderBy, orderDirection })
          }
          addData={(offset) => mutate({ offset, limit: POINTS_TABLE_LIMIT })}
        />
      ) : (
        <NoDataComponent />
      )}
    </>
  );
};

export default observer(BrandsTable);
